"use client";

import React, { useState, useRef } from "react";
import { CircleCheck, ChevronLeft, ChevronRight, CloudUpload, Wrench, FileText, User, CircleAlert, Phone, Mail, MapPin, CheckCircle2, Clock, ShieldCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const STEPS = [{
  id: 1,
  title: "Project Type",
  icon: Wrench
}, {
  id: 2,
  title: "Work Details",
  icon: FileText
}, {
  id: 3,
  title: "Contact Info",
  icon: User
}];
export default function RequestTechnicalSupport() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="245324" id="start-your-project" className="bg-background py-16 sm:py-24 lg:py-[110px] relative overflow-hidden text-foreground" ref={ref}>
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12 relative z-10">
        {/* Header Block matching WhyChooseFirststop eyebrow + heading style */}
        <div className={`text-center max-w-2xl mx-auto mb-12 md:mb-16 transition-all duration-1000 ease-out motion-reduce:transition-none motion-reduce:transform-none ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-xs md:text-sm font-bold uppercase tracking-widest text-primary mb-3">
            START YOUR PROJECT
          </p>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-secondary-foreground mb-4">
            Start Your Project
          </h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto font-default leading-relaxed">
            Ready to protect your home? Provide a few details about your roofing project in Southampton and Matt will get back to you with a professional quote.
          </p>
        </div>

        {/* Two-column layout: Form ~2/3, Contact Sidebar ~1/3 */}
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start transition-all duration-1000 delay-150 ease-out motion-reduce:transition-none motion-reduce:transform-none ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* LEFT: Multi-step quote form in rounded-[14px] card */}
          <div className="lg:col-span-8 bg-card border border-border rounded-[14px] shadow-[0_4px_20px_-2px_rgba(21,25,29,0.06)] overflow-hidden">
            <FormProvider formKey="start_project_form_get_in_touch" formId="start_project_form_get_in_touch" sectionName="start-your-project" defaultValues={{
            name: "",
            email: "",
            phone: "",
            equipment_model: "",
            serial_number: "",
            issue_description: "",
            preferred_date: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              minLength: 5
            },
            equipment_model: {
              required: true,
              minLength: 2
            },
            issue_description: {
              required: true,
              minLength: 10
            }
          }} submitText="Send Request" successMessage="Thank you for your enquiry. Matt will review your project details and contact you shortly.">
              <WizardForm />
            </FormProvider>
          </div>

          {/* RIGHT: Contact sidebar in charcoal (#15191D) rounded card */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28">
            <div className="rounded-[14px] bg-[#15191D] text-white p-7 md:p-8 border border-[#15191D] shadow-[0_4px_20px_-2px_rgba(21,25,29,0.12)]">
              <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
                DIRECT CONTACT
              </p>
              <h3 className="text-2xl font-bold font-default text-white mb-2">
                Talk to Matt
              </h3>
              <p className="text-white/80 text-sm leading-relaxed mb-6">
                Prefer to discuss your project straight away? Call or email Matt directly for friendly, experienced advice.
              </p>

              <div className="space-y-5 pt-2 border-t border-white/10">
                {/* Phone */}
                <div className="flex items-start gap-3.5">
                  <div aria-hidden="true" className="w-10 h-10 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0 text-primary mt-0.5">
                    <Phone className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <span className="block text-xs uppercase tracking-wider text-white/60 font-medium">
                      Call Matt
                    </span>
                    <a href="tel:07443802652" className="text-lg font-bold text-white hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm inline-block">
                      07443 802652
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-3.5">
                  <div aria-hidden="true" className="w-10 h-10 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0 text-primary mt-0.5">
                    <Mail className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="block text-xs uppercase tracking-wider text-white/60 font-medium">
                      Send an Email
                    </span>
                    <a href="mailto:Firststoproofinglimited@gmail.com" className="text-sm sm:text-base font-semibold text-white hover:text-primary transition-colors break-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm inline-block">
                      Firststoproofinglimited@gmail.com
                    </a>
                  </div>
                </div>

                {/* Service Area */}
                <div className="flex items-start gap-3.5 pt-2 border-t border-white/10">
                  <div aria-hidden="true" className="w-10 h-10 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0 text-primary mt-0.5">
                    <MapPin className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <span className="block text-xs uppercase tracking-wider text-white/60 font-medium">
                      Coverage
                    </span>
                    <p className="text-sm font-semibold text-white leading-snug">
                      Serving Southampton, Hampshire &amp; surrounding areas
                    </p>
                  </div>
                </div>
              </div>

              {/* Guarantees / Trust Badges */}
              <div className="mt-8 pt-6 border-t border-white/10 space-y-3">
                <div className="flex items-center gap-2.5 text-sm text-white/90">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" strokeWidth={2} />
                  <span>Free, no-obligation quotes</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-white/90">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" strokeWidth={2} />
                  <span>Honest advice &amp; fair pricing</span>
                </div>
                <div className="flex items-center gap-2.5 text-sm text-white/90">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" strokeWidth={2} />
                  <span>Clean, tidy and respectful on site</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}
function WizardForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [currentStep, setCurrentStep] = useState(1);
  const [fileUploaded, setFileUploaded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const progressValue = (currentStep - 1) / (STEPS.length - 1) * 100;
  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
    }
  };
  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFileUploaded(true);
    } else {
      setFileUploaded(false);
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="p-8 md:p-14 text-center flex flex-col items-center justify-center min-h-[420px]">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
          <CircleCheck className="w-8 h-8 text-primary" strokeWidth={2} />
        </div>
        <h3 className="text-2xl md:text-3xl font-default font-bold text-secondary-foreground mb-3">
          Enquiry Received
        </h3>
        <p className="text-muted-foreground font-default text-base md:text-lg mb-8 max-w-md leading-relaxed">
          {successMessage}
        </p>
        <Button onClick={() => {
        resetForm();
        setCurrentStep(1);
        setFileUploaded(false);
      }} variant="outline" className="min-h-[44px] px-6 rounded-lg border-primary text-primary hover:bg-primary/5 font-semibold">
          Submit Another Enquiry
        </Button>
      </div>;
  }
  return <div className="flex flex-col h-full">
      {/* Header step tracker with gold progress line */}
      <div className="bg-[#F7F5F1] border-b border-border p-6 md:px-8">
        <div className="flex justify-between items-center mb-4 sm:mb-6 relative">
          {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isCompleted = currentStep > step.id;
          return <div key={step.id} data-index={index} className="flex flex-col items-center relative z-10">
                <div className={`w-11 h-11 rounded-full flex items-center justify-center border-2 transition-all duration-300 font-bold text-sm ${isActive ? "bg-primary border-primary text-primary-foreground shadow-md ring-4 ring-primary/20" : isCompleted ? "bg-primary border-primary text-primary-foreground" : "bg-white border-border text-muted-foreground"}`}>
                  {isCompleted ? <CircleCheck className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} /> : <span className="font-bold">{step.id}</span>}
                </div>
                <span className={`text-xs font-default font-semibold mt-2.5 hidden sm:block transition-colors duration-300 ${isActive || isCompleted ? "text-secondary-foreground" : "text-muted-foreground"}`}>
                  {step.title}
                </span>
              </div>;
        })}

          {/* Background rail + Gold progress bar */}
          <div aria-hidden="true" className="absolute top-5 left-6 right-6 h-[3px] bg-border -z-0">
            <div className="h-full bg-primary transition-all duration-500 ease-in-out" style={{
            width: `${progressValue}%`
          }} />
          </div>
        </div>
        <Progress value={progressValue} className="h-1.5 sm:hidden bg-border [&>div]:bg-primary" />
      </div>

      {/* Form content */}
      <div className="p-6 md:p-8 lg:p-10 flex-grow">
        <Form onSubmit={handleSubmit} className="space-y-0">
          {/* STEP 1: Project Type */}
          <div className={`space-y-6 transition-opacity duration-300 ${currentStep === 1 ? "block opacity-100" : "hidden opacity-0"}`}>
            <div className="mb-6">
              <h3 className="text-xl md:text-2xl font-default font-bold text-secondary-foreground">
                Project Details
              </h3>
              <p className="text-sm text-muted-foreground font-default mt-1">
                Tell us what kind of work you are looking for.
              </p>
            </div>

            <FormField control={control} name="equipment_model" rules={{
            required: "Service type is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-semibold text-secondary-foreground text-sm">
                    Service Required <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. New Roofs, Fascias & Soffits, Guttering, Lead Work" className="h-12 rounded-lg border-border focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary text-base px-4" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="serial_number" render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-semibold text-secondary-foreground text-sm">
                    Property Location
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Postcode or town (e.g. Southampton, SO15)" className="h-12 rounded-lg border-border focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary text-base px-4" {...field} />
                  </FormControl>
                  <FormDescription className="font-default text-xs text-muted-foreground">
                    Helps us schedule a site visit and estimate travel.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          {/* STEP 2: Work Description */}
          <div className={`space-y-6 transition-opacity duration-300 ${currentStep === 2 ? "block opacity-100" : "hidden opacity-0"}`}>
            <div className="mb-6">
              <h3 className="text-xl md:text-2xl font-default font-bold text-secondary-foreground">
                Work Description
              </h3>
              <p className="text-sm text-muted-foreground font-default mt-1">
                Describe the scope of work and any specific requirements.
              </p>
            </div>

            <FormField control={control} name="issue_description" rules={{
            required: "Project description is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-semibold text-secondary-foreground text-sm">
                    Project Scope <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea placeholder="Please describe the approximate size, materials, current condition, or any specific issues you'd like us to address..." className="min-h-[140px] resize-y rounded-lg border-border focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary text-base p-4" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="preferred_date" render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-semibold text-secondary-foreground text-sm">
                    Preferred Start Date
                  </FormLabel>
                  <FormControl>
                    <Input type="date" className="h-12 rounded-lg border-border focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary text-base px-4" {...field} />
                  </FormControl>
                  <FormDescription className="font-default text-xs text-muted-foreground">
                    When would you like the work to commence?
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />

            {/* Photo upload */}
            <div className="space-y-2">
              <FormLabel className="font-semibold text-secondary-foreground text-sm">
                Attach Site Photos (Optional)
              </FormLabel>
              <div role="button" tabIndex={0} aria-label="Click to upload photos of the area" className={`border-2 border-dashed rounded-lg p-6 sm:p-8 text-center transition-all cursor-pointer ${fileUploaded ? "border-primary bg-primary/5" : "border-border hover:border-primary/60 hover:bg-primary/[0.02]"}`} onClick={() => fileInputRef.current?.click()} onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}>
                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
                <CloudUpload className={`w-10 h-10 mx-auto mb-2.5 transition-colors ${fileUploaded ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.75} />
                <p className="text-sm font-default font-semibold text-secondary-foreground">
                  {fileUploaded ? "Photos attached successfully" : "Click to upload photos of the area"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  JPG or PNG up to 10MB
                </p>
              </div>
            </div>
          </div>

          {/* STEP 3: Contact Info */}
          <div className={`space-y-6 transition-opacity duration-300 ${currentStep === 3 ? "block opacity-100" : "hidden opacity-0"}`}>
            <div className="mb-6">
              <h3 className="text-xl md:text-2xl font-default font-bold text-secondary-foreground">
                Contact Information
              </h3>
              <p className="text-sm text-muted-foreground font-default mt-1">
                How can Matt reach you to discuss your free quote?
              </p>
            </div>

            <FormField control={control} name="name" rules={{
            required: "Name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-semibold text-secondary-foreground text-sm">
                    Your Name <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. John Smith" className="h-12 rounded-lg border-border focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary text-base px-4" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="email" rules={{
              required: "Email is required",
              pattern: {
                value: /^\S+@\S+\.\S+$/,
                message: "Invalid email address"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-semibold text-secondary-foreground text-sm">
                      Email Address <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="john@example.co.uk" className="h-12 rounded-lg border-border focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary text-base px-4" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="phone" rules={{
              required: "Phone number is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-semibold text-secondary-foreground text-sm">
                      Phone Number <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="07443 802652" className="h-12 rounded-lg border-border focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary text-base px-4" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3 mt-6">
                <CircleAlert className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                <p className="text-sm text-destructive font-medium">{submitError}</p>
              </div>}
          </div>

          {/* Navigation buttons */}
          <div className="mt-8 pt-6 border-t border-border flex items-center justify-between gap-4">
            <Button type="button" variant="outline" onClick={handlePrev} disabled={currentStep === 1 || isSubmitting} className={`min-h-[44px] px-5 rounded-lg border-border hover:border-primary text-foreground hover:bg-primary/5 font-semibold transition-colors ${currentStep === 1 ? "invisible" : ""}`}>
              <ChevronLeft className="w-4 h-4 mr-1.5" />
              Back
            </Button>

            {currentStep < STEPS.length ? <Button type="button" onClick={handleNext} className="ml-auto min-h-[44px] px-6 rounded-lg bg-primary text-primary-foreground hover:bg-primary-hover font-semibold shadow-sm transition-colors">
                Next Step
                <ChevronRight className="w-4 h-4 ml-1.5" />
              </Button> : <Button type="submit" disabled={isSubmitting} className="ml-auto min-h-[44px] px-8 rounded-lg bg-primary text-primary-foreground hover:bg-primary-hover font-semibold shadow-sm transition-colors">
                {isSubmitting ? <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </span> : "Send Request"}
              </Button>}
          </div>
        </Form>
      </div>
    </div>;
}